import { GuitarLib } from '@tunasong/music-lib'
import type { Scale } from '@tunasong/schemas'
import { createVirtualElement, Divider, Popup, useDelayedValue, type VirtualElement, Button, Alert, AlertTitle, ButtonGroup, FormControlLabel, FormLabel, Grid, Checkbox, Typography, Radio, RadioGroup} from '@tunasong/ui-lib'
import { useState, type FC, type MouseEvent } from 'react'
import NoteInfo from '../note/note-info.js'
import Fretboard from './visualization/fretboard.js'
import styles from './guitar-patterns.module.css'

export interface GuitarPatternsProps {
  scale?: Scale | null
  groups?: GuitarLib.GuitarPatternSpecGroup[]
  selectedPattern?: {
    group: GuitarLib.GuitarPatternSpecGroup
    patternIdx: number
  }
  onSelectedPattern: (pattern: { group: GuitarLib.GuitarPatternSpecGroup; patternIdx: number }) => void
}

export const GuitarPatterns: FC<GuitarPatternsProps> = props => {
  const { scale, groups = [], selectedPattern, onSelectedPattern } = props

  const [allowTruncateSteps, setallowTruncateSteps] = useState(0)
  const [allowOpenStrings, setAllowOpenStrings] = useState(false)
  const [colors, setColors] = useState(true)
  const [infoOnHover, setInfoOnHover] = useState(true)

  const generatedGroup =
    selectedPattern && scale
      ? GuitarLib.generateScalePatternGroup({
          group: selectedPattern.group,
          rootScale: scale,
          startFret: 0,
          endFret: 30,
          allowOpenStrings,
          allowTruncateSteps,
        })
      : undefined
  const pattern = generatedGroup && selectedPattern ? generatedGroup.shapes[selectedPattern.patternIdx] : undefined

  const [fretboardLabel, setFretboardLabel] = useState<'note' | 'finger' | 'degree' | 'none'>('note')

  const fingerings = pattern?.fingerings && scale ? GuitarLib.getFingeringWithMetadata(pattern?.fingerings, scale) : []

  const [activeFingering, setActiveFingering] = useState<GuitarLib.FingeringWithMetadata>()
  const [popperPos, setPopperPos] = useState<VirtualElement | null>(null)
  const handleHoverFingering = (ev: MouseEvent, { str, fret }: GuitarLib.Fingering) => {
    if (!infoOnHover) {
      return
    }
    // Find the fingering that matches the string and fret
    const fingering = fingerings.find(f => f.str === str && f.fret === fret)
    setActiveFingering(fingering)
    setPopperPos(createVirtualElement(ev))
  }

  // Delay the popper
  const delayedPopperPos = useDelayedValue(popperPos, 500) ?? null
  const open = Boolean(activeFingering) && delayedPopperPos === popperPos

  return (
    <>
      {groups.map(group => (
        <div key={group.name} className={styles.groupSection}>
          <h3>{group.name}</h3>
          <Typography variant="body2" className={styles.groupDescription}>
            {group.description}
          </Typography>
          {group.shapes.map((shapePattern, patternIdx) => (
            <ButtonGroup key={shapePattern.name} variant="outlined" className={styles.buttonGroup}>
              <Button
                variant={shapePattern?.name === pattern?.name ? 'contained' : 'outlined'}
                onClick={() => {
                  onSelectedPattern({ group, patternIdx })
                }}
              >
                {shapePattern.name}
              </Button>
            </ButtonGroup>
          ))}
        </div>
      ))}

      <Divider className={styles.divider} />
      <Grid container spacing={2} className={styles.optionsGrid}>
        <Grid size={{ xs: 6 }}>
          <FormLabel className={styles.formLabel}>
            Fretboard Labels
          </FormLabel>
          <RadioGroup
            row
            value={fretboardLabel}
            onChange={e => setFretboardLabel(e.target.value as 'note' | 'finger' | 'degree')}
            className={styles.radioGroup}
          >
            <FormControlLabel value="none" control={<Radio />} label="None" />
            <FormControlLabel value="note" control={<Radio />} label="Note" />
            <FormControlLabel value="finger" control={<Radio />} label="Finger" />
            <FormControlLabel value="degree" control={<Radio />} label="Scale Degree" />
          </RadioGroup>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={allowTruncateSteps === -1}
                onChange={e => setallowTruncateSteps(e.target.checked ? -1 : 0)}
              />
            }
            label="Allow shape to be truncated in first position"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={!!allowOpenStrings}
                onChange={e => {
                  setAllowOpenStrings(e.target.checked)
                }}
              />
            }
            label="Allow open strings"
          />
          <FormControlLabel
            control={<Checkbox checked={colors} onChange={e => setColors(e.target.checked)} />}
            label="Show colors"
          />
          <FormControlLabel
            control={<Checkbox checked={infoOnHover} onChange={e => setInfoOnHover(e.target.checked)} />}
            label="Note information on hover"
          />
        </Grid>
      </Grid>
      {pattern ? (
        <div className={styles.patternInfo}>
          {pattern.name}
          {' - '} {pattern?.description}
        </div>
      ) : null}
      <div className={styles.fretboardContainer}>
        <Fretboard
          start={1}
          end={21}
          fingerings={fingerings}
          scale={scale}
          colors={colors}
          labels={fretboardLabel}
          onHoverFingering={handleHoverFingering}
        />
      </div>

      <Popup open={open} anchorEl={delayedPopperPos} onClose={() => setActiveFingering(undefined)}>
        <Alert severity="info">
          <AlertTitle>Note information</AlertTitle>
          {scale ? <NoteInfo note={activeFingering?.note} scale={scale} /> : null}
        </Alert>
      </Popup>
    </>
  )
}
